import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, CalendarDays, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { getEmbedUrl } from "@/lib/stream";
import TeamCrest from "@/components/TeamCrest";
import Countdown from "@/components/Countdown";
import PotgCard from "@/components/PotgCard";
import LiveStreamPlayer from "@/components/LiveStreamPlayer";

export default function Live() {
    const [liveGames, setLiveGames] = useState(null);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        const load = () => {
            api.get("/games", { params: { status: "live" } })
                .then((r) => setLiveGames(r.data))
                .catch(() => setLiveGames([]));
            api.get("/league/summary").then((r) => setSummary(r.data)).catch(() => {});
        };
        load();
        const t = setInterval(load, 6000);
        return () => clearInterval(t);
    }, []);

    return (
        <div data-testid="live-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#EF4444] mb-3">
                <Radio className="w-4 h-4" /> Courtside Broadcast
            </p>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white">
                Live <span className="text-stroke-faint">Coverage</span>
            </h1>

            {liveGames === null ? (
                <p className="mt-16 text-slate-500 font-stat text-sm">Tuning in…</p>
            ) : liveGames.length > 0 ? (
                liveGames.map((g) => <LiveGame key={g.id} game={g} />)
            ) : (
                <NoLive summary={summary} />
            )}
        </div>
    );
}

function LiveGame({ game }) {
    const embed = getEmbedUrl(game.stream_url);
    const [cameraLive, setCameraLive] = useState(false);
    return (
        <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid={`live-game-${game.id}`}
            className="mt-10 card-arena rounded-3xl overflow-hidden"
        >
            <div className="relative p-6 sm:p-10">
                <div className="spotlight spotlight-ember top-[-40%] right-[-15%] opacity-50" />
                <div className="relative">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                        <span className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/50">
                            <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" /> Live
                        </span>
                        <span data-testid={`live-clock-${game.id}`} className="font-stat font-bold text-[#FFB800] text-lg">
                            {game.quarter || "—"}
                            {game.clock ? ` • ${game.clock}` : ""}
                        </span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                        <div className="flex flex-col items-center gap-3 text-center">
                            <TeamCrest team={game.home_team} size="xl" />
                            <span className="font-display font-bold uppercase text-white text-base sm:text-lg leading-tight">
                                {game.home_team?.name}
                            </span>
                        </div>
                        <div data-testid={`live-score-${game.id}`} className="font-stat font-bold text-5xl sm:text-7xl text-center">
                            <span className="text-[#EF4444]">{game.home_score ?? 0}</span>
                            <span className="text-slate-700 mx-2 sm:mx-3">:</span>
                            <span className="text-[#EF4444]">{game.away_score ?? 0}</span>
                        </div>
                        <div className="flex flex-col items-center gap-3 text-center">
                            <TeamCrest team={game.away_team} size="xl" />
                            <span className="font-display font-bold uppercase text-white text-base sm:text-lg leading-tight">
                                {game.away_team?.name}
                            </span>
                        </div>
                    </div>
                    {game.venue && (
                        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500 mt-8">
                            <MapPin className="w-3.5 h-3.5 text-[#FF5500]" /> {game.venue}
                            {game.week ? ` • Week ${game.week}` : ""}
                        </p>
                    )}
                </div>
            </div>
            <LiveStreamPlayer game={game} onActive={setCameraLive} />
            {!cameraLive &&
                (embed ? (
                    <div className="aspect-video bg-black border-t border-[#262638]">
                        <iframe
                            src={embed}
                            title="Live game stream"
                            data-testid={`live-stream-${game.id}`}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                ) : (
                    <p className="border-t border-[#262638] px-6 py-5 text-center text-sm text-slate-500">
                        No camera broadcast or stream link yet — the scoreboard above updates automatically.
                    </p>
                ))}
        </motion.section>
    );
}

function NoLive({ summary }) {
    const next = summary?.next_game;
    const finals = (summary?.recent_results || []).filter((g) => g.player_of_the_game);
    return (
        <div data-testid="live-empty" className="mt-10 space-y-12">
            <div className="card-arena rounded-3xl p-10 text-center">
                <p className="font-display font-bold uppercase text-2xl text-slate-300">No games live right now</p>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                    When the league goes live, the stream and the real-time scoreboard land right here.
                </p>
                {next && (
                    <div className="mt-8 inline-flex flex-col items-center gap-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FF5500]">
                            Next tip-off
                        </p>
                        <div className="flex items-center gap-4">
                            <TeamCrest team={next.home_team} size="md" />
                            <span className="font-display font-black text-2xl text-stroke-faint">VS</span>
                            <TeamCrest team={next.away_team} size="md" />
                        </div>
                        <Countdown target={next.date} testid="live-next-countdown" />
                        <Link
                            to="/schedule"
                            data-testid="live-cta-schedule"
                            className="flex items-center gap-2 text-[#FF5500] hover:text-[#FFB800] font-display font-bold uppercase tracking-widest text-sm transition-colors duration-200"
                        >
                            <CalendarDays className="w-4 h-4" /> Full schedule
                        </Link>
                    </div>
                )}
            </div>
            {finals.length > 0 && (
                <div>
                    <h2 className="font-display font-black uppercase text-2xl text-white mb-5">
                        Recent <span className="text-[#FFB800]">Players of the Game</span>
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {finals.map((g) => (
                            <div key={g.id} className="card-arena rounded-2xl p-5">
                                <p className="text-xs font-stat text-slate-400">
                                    {g.home_team?.short_name} {g.home_score} — {g.away_score} {g.away_team?.short_name} •
                                    Final
                                </p>
                                <PotgCard game={g} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
